import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { AngularFireStorage } from '@angular/fire/compat/storage';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { EnqueteDownloadFile } from '../firestore-models/enquete-download-file';
import { ApiEnqueteDownloadFile, ApiCreateEnqueteDownloadFilesRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';
import { TranslateService } from '@ngx-translate/core';

import { EnqueteDialogComponent } from './enquete-dialog/enquete-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';

@Component({
  selector: 'app-enquete',
  templateUrl: './enquete.component.html',
  styleUrls: ['./enquete.component.css'],
  standalone: false,
})
export class EnqueteComponent implements OnInit {

  //EnqueteDownloadFileコレクション
  private enqueteDownloadFilesCollection!: AngularFirestoreCollection<EnqueteDownloadFile>;
  enqueteDownloadFilesObservable!: Observable<EnqueteDownloadFile[]>;
  enqueteDownloadFilesSubscribe!: Subscription;
  //プロジェクトID
  projectId: string;

  //アンケートダウンロードファイル情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'created', 'memo', 'start_date', 'end_date', 'count', 'download'];
  dataSource = new MatTableDataSource<EnqueteDownloadFile>();
  selection = new SelectionModel<EnqueteDownloadFile>(true, []);
  element!: HTMLElement;

  currentLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private angularFireStorage: AngularFireStorage,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.enqueteDownloadFilesCollection = this.angularFirestore.collection<EnqueteDownloadFile>(
      AppConst.FIRESTORE_ENQUETE_DOWNLOAD_FILES, ref => ref.where('project_id', '==', this.projectId).orderBy('created', 'desc'));
    this.enqueteDownloadFilesObservable = this.enqueteDownloadFilesCollection.valueChanges({ idField: 'id' });
    this.enqueteDownloadFilesSubscribe = this.enqueteDownloadFilesObservable.subscribe(enqueteDownloadFiles => {
      this.dataSource.data = this.addFilterValue(enqueteDownloadFiles);
    });
    this.currentLang = this.translate.currentLang;
  }

  ngOnDestroy(): void {
    this.enqueteDownloadFilesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: EnqueteDownloadFile): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(enqueteDownloadFile: EnqueteDownloadFile): void {
    const startDate: any = enqueteDownloadFile!.start_date ? new Date(enqueteDownloadFile!.start_date.seconds * 1000) : null;
    const endDate: any = enqueteDownloadFile!.end_date ? new Date(enqueteDownloadFile!.end_date.seconds * 1000) : null;
    let apiEnqueteDownloadFile: ApiEnqueteDownloadFile = {
      id: enqueteDownloadFile.id,
      start_date: startDate,
      end_date: endDate,
      memo: enqueteDownloadFile.memo,
      quest_set_id: enqueteDownloadFile.quest_set_id,
      project_id: enqueteDownloadFile.project_id,
      enabled: enqueteDownloadFile.enabled
    };
    const dialogRef = this.dialog.open(EnqueteDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, enqueteDownloadFile: apiEnqueteDownloadFile }
    });
}

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(EnqueteDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, enqueteDownloadFile: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedEnqueteDownloadFiles: Array<ApiEnqueteDownloadFile> = new Array<ApiEnqueteDownloadFile>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedEnqueteDownloadFile: ApiEnqueteDownloadFile = {
            id: row.id,
            start_date: row.start_date.toDate(),
            end_date: row.end_date.toDate(),
            memo: row.memo,
            quest_set_id: row.quest_set_id,
            project_id: row.project_id,
            enabled: row.enabled
          };
          selectedEnqueteDownloadFiles.push(selectedEnqueteDownloadFile);
        }
      }
      if (!!result) {
        let request: ApiCreateEnqueteDownloadFilesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          enquete_download_files: selectedEnqueteDownloadFiles
        }
        this.spinnerService.openSpinner();
        this.apiClientService.createEnqueteDownloadFiles(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'memo':
          return compare(a.memo, b.memo, isAsc);
        case 'count':
          return compare(a.row_count, b.row_count, isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      filterValue += doc.memo + ' '
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  download(enqueteDownloadFile: EnqueteDownloadFile): void {
    const path = `enquete/${enqueteDownloadFile.project_id}/${enqueteDownloadFile.file_name}`;
    const storageRef = this.angularFireStorage.ref(path);
    const url = storageRef.getDownloadURL().subscribe((url) => {
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.download = enqueteDownloadFile.file_name!;
      a.href = url!;
      a.click();
      document.body.removeChild(a);
    });
  }

}
